import { FileWithPreview } from "app/components/AudioFileUploader";
import axios from "axios";
import { TaskQueue } from "cwait";
import { useEffect, useState, useCallback } from "react";

// This hook handles all the logic for listing input audio files after we have signed urls
// tracking upload progress and duration of each audio file, and uploading files to controlla storage

// Components that use this hook can focus on UI and logic of the rest of the page, while this component handles 
// an audio file list state

// CURRENT PARENT REQUIREMENTS:
// - This hook requires the parent component to call handleLoadedMetadata to update 
//   an audio file duration from a Waveform component's onLoadedMetadata event

type AudioFileListUploaderStateAndActions = {
    // state
    audioFiles: Array<FileWithPreview>;
    totalUploadProgress: number;
    isUploading: boolean;
    // actions
    handleLoadedMetadata: (e: any, audioKey: any, durationOverride?: any) => void;
    setAudioFiles: (files: Array<FileWithPreview> | ((prev: Array<FileWithPreview>) => Array<FileWithPreview>)) => void;
    // computed
    isAllUploadsComplete: () => boolean;
    getTotalDuration: () => number;
};

export function useAudioFilesListUploader(): AudioFileListUploaderStateAndActions {
    //
    //
    //    STATE
    //
    //

    // Init audioFiles to empty array, append files as they are selected or added
    const [audioFiles, setAudioFiles] = useState<Array<FileWithPreview>>([]);
    // calc total upload progress
    // @ts-ignore
    const totalUploadProgress = audioFiles
        .map(audio => audio.uploadProgress)
        // @ts-ignore
        .reduce((partialSum, a) => partialSum + a, 0) / audioFiles.length;

    // PREVIOUSLY CALLED isUploading from InferPageparent
    const hasMorePartialUploads = audioFiles.length > 0 && totalUploadProgress != 1;
    const isUploading = hasMorePartialUploads;

    //
    //
    //    ACTIONS
    //
    //

    // PUBLIC Called from a child Waveform component when an audio file is loaded
    // to update duration for that audio file, record in browser will pass durationOverride 
    // using lastRecordingTime from the react-audio-recorder package 
    const handleLoadedMetadata = (e, audioKey, durationOverride) => {
        let { duration } = e.target;
        if (durationOverride) duration = durationOverride

        // infinite duration when using record in browser, handler is manually triggered again with correct duration
        if (duration === Infinity) {
          console.log("infinite duration audio file, skipping writing duration")
        } else {
          // set duration on the FilePreview object in audiofiles array
          setPropertyOnAudioFilesById("duration", duration, audioKey)
        }
    };

    // PRIVATE called from useEffect to update uploadProgress for a specific audioFile
    const setAudioFileUploadProgress = useCallback((key, progress) => {
        setAudioFiles(prevAudioFiles => {
          if (prevAudioFiles.length === 0) console.error('no audio files');
          let newAudioFiles: Array<FileWithPreview> = [];
          for (let audioFile of prevAudioFiles) {
            const newAudioFile = Object.assign({}, audioFile);
            if (newAudioFile.key === key) {
              newAudioFile.uploadProgress = progress;
            }
            newAudioFiles.push(newAudioFile);
          }
          console.log(newAudioFiles);
          // onAudioFilesChange && onAudioFilesChange(newAudioFiles);
          return newAudioFiles;
        });
    }, []);

    // PRIVATE Set all audiofiles to uploadProgress 0 so the useEffect audio uploader knows theyve already been queued
    const setPropertyOnAudioFilesUnderCond = useCallback((key, value, condition) => {
        setAudioFiles(prevAudioFiles => {
            if (prevAudioFiles.length === 0) console.error('no audio files');
            let newAudioFiles: Array<FileWithPreview> = [];
            for (let audioFile of prevAudioFiles) {
                const newAudioFile = Object.assign({}, audioFile);
                if (condition(audioFile[key])) {
                    newAudioFile[key] = value;
                }
                newAudioFiles.push(newAudioFile);
            }
            // onAudioFilesChange && onAudioFilesChange(newAudioFiles);
            return newAudioFiles;
        });
    }, []);

    // PRIVATE Set a property and value on an audioFile with this key in audiofiles
    // currently used for setting duration on both file uploads and record in browser
    const setPropertyOnAudioFilesById = useCallback((property, value, key) => {
      setAudioFiles(prevAudioFiles => {
          if (prevAudioFiles.length === 0) console.error('no audio files');
          let newAudioFiles: Array<FileWithPreview> = [];
          for (let audioFile of prevAudioFiles) {
              const newAudioFile = Object.assign({}, audioFile);
              if (audioFile.key === key) {
                  newAudioFile[property] = value;
              }
              newAudioFiles.push(newAudioFile);
          }
          // onAudioFilesChange && onAudioFilesChange(newAudioFiles);
          return newAudioFiles;
      });
  }, []);


    //
    //
    //    EFFECTS
    //
    //

    // Once we get files from client with signed Urls from server, upload to google storage
    // signed urls happen in the AudioSelectButtonModal
    useEffect(() => {
        let hasMoreUploadsToStart = false;
        for (const audioFile of audioFiles) {
            // Once audio files are queued, uploadProgress is set to 0 to avoid reuploading
            if (audioFile.uploadProgress === null) {
                hasMoreUploadsToStart = true;
            }
        }

        if (!hasMoreUploadsToStart) return;


        async function uploadAudioFiles() {
            const MAX_PARALLEL_UPLOADS = 5;
            // https://github.com/axios/axios/issues/1010
            const queue = new TaskQueue(Promise as any, MAX_PARALLEL_UPLOADS);

            // Set all audiofiles to uploadProgress 0 so they don't get uploaded again in this useEffect
            setPropertyOnAudioFilesUnderCond('uploadProgress', 0, v => v == null);

            // Start uploading to signed urls
            await Promise.all(
                audioFiles.map(
                queue.wrap(async audio => {
                    try {
                        if (audio.uploadProgress === null) {
                            const upload = await axios.put(audio.signedUrl, audio.data, {
                              headers: {
                                  'Content-Type': 'application/octet-stream',
                              },
                              onUploadProgress: p => {
                                  setAudioFileUploadProgress(audio.key, p.progress);
                              },
                            });
                            if (upload.status === 200) {
                              console.log(upload);
                            }
                        }
                    } catch (e) {
                        console.error(e);
                    }
                }),
                ),
            );

        }
        uploadAudioFiles();
    }, [audioFiles, setAudioFileUploadProgress, setPropertyOnAudioFilesUnderCond]);


    //
    //
    //  COMPUTED  - Functions for derived values 
    //
    //
    const isAllUploadsComplete = () => {
        for (const audioFile of audioFiles) {
          if (audioFile.uploadProgress !== 1) {
            return false;
          }
        }
        return true;
    };

    const getTotalDuration = () => {
      let totalDuration = 0
      for (const audioFile of audioFiles) {
        if (audioFile.duration) {
          totalDuration += audioFile.duration
        }
      }
      return totalDuration
    }



    return {
        //
        // STATE
        //

        // audioFiles state
        audioFiles,
        totalUploadProgress,
        isUploading,
        
        //
        // ACTIONS
        //
        handleLoadedMetadata,
        setAudioFiles,

        //
        // COMPUTED
        //
        isAllUploadsComplete,
        getTotalDuration,
    };
      
}
